import { Chart } from 'chart.js/auto';
import store from '../../../../state';
import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';
import { ForecastBinaryService, DecodedPointForecast, seeingScoreToArcsec } from '../../../../services/ForecastBinaryService';
import TierAccessService from '../../../../services/TierAccessService';

export interface SeriesModelOption {
  id: string;
  name: string;
}

export const DEFAULT_SERIES_LABELS: Record<string, string> = {
  temps_f: 'Temperature at Forecast Point (°F)',
  temps_c: 'Temperature at Forecast Point (°C)',
  temps_highest_f: 'Summit / Ridge Peak Temp (°F)',
  temps_mid_f: 'Mid-Mountain / Tree Line Temp (°F)',
  temps_lowest_f: 'Valley Floor / Base Temp (°F)',
  dewpoint: 'Dew Point Forecast',
  dew_point_f: 'Dew Point (°F)',
  windchill_f: 'Wind Chill Index (°F)',
  cloud_cover_total: 'Total Cloud Cover (%)',
  cloud_cover_low: 'Low Stratus Clouds (%)',
  cloud_cover_mid: 'Mid Altocumulus Clouds (%)',
  cloud_cover_high: 'High Cirrus Clouds (%)',
  cloud_base_ft: 'Cloud Base / Ceiling (ft MSL)',
  cloud_top_ft: 'Cloud Top Altitude (ft MSL)',
  freezing_level_ft: 'Freezing Level (ft MSL)',
  wind_speed_mph: 'Sustained Wind Speed (mph)',
  wind_gust_mph: 'Peak Wind Gusts (mph)',
  wind_dir_deg: 'Wind Direction (°)',
  precip_rate_in_h: 'Precipitation Rate (in/hr)',
  precip_prob_pct: 'Precipitation Probability (%)',
  thunderstorm_prob: 'Thunderstorm Probability (%)',
  lightning_hazard: 'Lightning Threat Hazard',
  snow_rate_in_h: 'Snowfall Accumulation Rate (in/hr)',
  darkskies_score: 'Dark Skies Score (0-10)',
  seeing_arcsec: 'Astronomical Seeing (arcsec)',
  optics_dew_risk_score: 'Optics Dew Risk (0-100)',
  relative_humidity_pct: 'Relative Humidity (%)',
  sun_intensity_wm2: 'Solar Irradiance (W/m²)',
  aqi_values: 'Air Quality Index (AQI)',
  smoke_density_ugm3: 'Ground Smoke PM2.5 (µg/m³)'
};

export const SERIES_SUPPORTED_MODELS: Record<string, { label: string; default: string; models: SeriesModelOption[] }> = {
  seeing: {
    label: 'Seeing',
    default: 'rdps',
    models: [
      { id: 'rdps', name: 'RDPS (10km)' },
      { id: 'hrrr', name: 'HRRR (3km)' },
      { id: 'rrfs', name: 'RRFS (3km)' },
      { id: 'ecmwf', name: 'ECMWF (IFS)' },
      { id: 'gfs', name: 'GFS (0.25°)' },
      { id: 'icon13', name: 'ICON-13' }
    ]
  },
  darkskies: {
    label: 'Dark Skies',
    default: 'blend',
    models: [
      { id: 'blend', name: 'Blend' },
      { id: 'hrrr', name: 'HRRR (3km)' },
      { id: 'rrfs', name: 'RRFS (3km)' },
      { id: 'rdps', name: 'RDPS (10km)' },
      { id: 'gdps', name: 'GDPS (15km)' },
      { id: 'ecmwf', name: 'ECMWF (IFS)' },
      { id: 'gfs', name: 'GFS (0.25°)' },
      { id: 'icon13', name: 'ICON-13' }
    ]
  },
  dew: {
    label: 'Dew Risk',
    default: 'hrrr',
    models: [
      { id: 'hrrr', name: 'HRRR (3km)' },
      { id: 'rrfs', name: 'RRFS (3km)' },
      { id: 'rdps', name: 'RDPS (10km)' },
      { id: 'gdps', name: 'GDPS (15km)' },
      { id: 'ecmwf', name: 'ECMWF (IFS)' },
      { id: 'gfs', name: 'GFS (0.25°)' },
      { id: 'icon13', name: 'ICON-13' }
    ]
  },
  temps: {
    label: 'Temperature',
    default: 'auto',
    models: [
      { id: 'auto', name: 'Auto' },
      { id: 'hrrr', name: 'HRRR (3km)' },
      { id: 'rrfs', name: 'RRFS (3km)' },
      { id: 'rdps', name: 'RDPS (10km)' },
      { id: 'gdps', name: 'GDPS (15km)' },
      { id: 'ecmwf', name: 'ECMWF (IFS)' },
      { id: 'gfs', name: 'GFS (0.25°)' },
      { id: 'icon13', name: 'ICON-13' }
    ]
  },
  clouds: {
    label: 'Cloud Cover',
    default: 'auto',
    models: [
      { id: 'auto', name: 'Auto' },
      { id: 'hrrr', name: 'HRRR (3km)' },
      { id: 'rrfs', name: 'RRFS (3km)' },
      { id: 'rdps', name: 'RDPS (10km)' },
      { id: 'gdps', name: 'GDPS (15km)' },
      { id: 'ecmwf', name: 'ECMWF (IFS)' },
      { id: 'gfs', name: 'GFS (0.25°)' },
      { id: 'icon13', name: 'ICON-13' }
    ]
  },
  precip: {
    label: 'Precipitation',
    default: 'auto',
    models: [
      { id: 'auto', name: 'Auto' },
      { id: 'hrrr', name: 'HRRR (3km)' },
      { id: 'rrfs', name: 'RRFS (3km)' },
      { id: 'rdps', name: 'RDPS (10km)' },
      { id: 'gdps', name: 'GDPS (15km)' },
      { id: 'ecmwf', name: 'ECMWF (IFS)' },
      { id: 'gfs', name: 'GFS (0.25°)' },
      { id: 'icon13', name: 'ICON-13' }
    ]
  },
  wind: {
    label: 'Wind Speed',
    default: 'auto',
    models: [
      { id: 'auto', name: 'Auto' },
      { id: 'hrrr', name: 'HRRR (3km)' },
      { id: 'rrfs', name: 'RRFS (3km)' },
      { id: 'rdps', name: 'RDPS (10km)' },
      { id: 'gdps', name: 'GDPS (15km)' },
      { id: 'ecmwf', name: 'ECMWF (IFS)' },
      { id: 'gfs', name: 'GFS (0.25°)' },
      { id: 'icon13', name: 'ICON-13' }
    ]
  },
  sun_intensity: {
    label: 'Solar Intensity',
    default: 'auto',
    models: [
      { id: 'auto', name: 'Auto' },
      { id: 'rrfs', name: 'RRFS (3km)' },
      { id: 'rdps', name: 'RDPS (10km)' },
      { id: 'gfs', name: 'GFS (0.25°)' },
      { id: 'gdps', name: 'GDPS (15km)' },
      { id: 'icon13', name: 'ICON-13' }
    ]
  }
};

export function getSeriesModelCategory(rawKey: string): string | null {
  const k = rawKey.toLowerCase();
  if (k.includes('seeing')) return 'seeing';
  if (k.includes('darkskies') || k.includes('darksky')) return 'darkskies';
  if (k.includes('dew_risk') || k.includes('dew')) return 'dew';
  if (k.includes('temp')) return 'temps';
  if (k.includes('cloud')) return 'clouds';
  if (k.includes('precip') || k.includes('rain')) return 'precip';
  if (k.includes('wind')) return 'wind';
  if (k.includes('sun_intensity') || k.includes('solar') || k.includes('irradiance') || k.includes('sun_flux')) return 'sun_intensity';
  return null;
}

function extractSeriesDataForModel(
  rawKey: string,
  pf: DecodedPointForecast,
  targetLength: number,
  timelineStartMs: number | undefined,
  isImperial: boolean
): number[] {
  const result: number[] = [];
  const baseEpochMs = timelineStartMs || (pf.cycleEpoch * 1000);

  for (let h = 0; h < targetLength; h++) {
    const targetUtcSec = Math.floor((baseEpochMs + h * 3600 * 1000) / 1000);
    const hourDiff = Math.floor((targetUtcSec - pf.cycleEpoch) / 3600);
    const sIdx = ForecastBinaryService.getStepIndexForHour(hourDiff, pf.numHours);

    if (rawKey.includes('seeing')) {
      if (pf.seeingScore && pf.seeingScore[sIdx] !== undefined) {
        result.push(Number(seeingScoreToArcsec(pf.seeingScore[sIdx]).toFixed(2)));
      } else {
        const ds = pf.darkskiesScore?.[sIdx] ?? 0;
        result.push(Number((1.0 + (10.0 - (ds > 10 ? ds / 10 : ds)) * 0.25).toFixed(2)));
      }
    } else if (rawKey.includes('darkskies') || rawKey.includes('darksky')) {
      const ds = pf.darkskiesScore?.[sIdx] ?? 0;
      result.push(ds > 10 ? Number((ds / 10).toFixed(1)) : Number(ds.toFixed(1)));
    } else if (rawKey.includes('dew_risk') || rawKey.includes('dew')) {
      result.push(pf.dewRiskScore?.[sIdx] ?? 0);
    } else if (rawKey.includes('temps') || rawKey.includes('temp')) {
      const c = pf.tempsC?.[sIdx] ?? 0;
      result.push(isImperial ? Math.round(c * 1.8 + 32) : Math.round(c));
    } else if (rawKey.includes('cloud')) {
      result.push(pf.cloudCoverTotal?.[sIdx] ?? 0);
    } else if (rawKey.includes('precip_prob') || rawKey.includes('precip_likelihood') || rawKey.includes('rain')) {
      result.push(pf.precipProb?.[sIdx] ?? 0);
    } else if (rawKey.includes('precip_rate') || rawKey.includes('rain_rate')) {
      const mm = pf.precipRateMmH?.[sIdx] ?? 0;
      result.push(isImperial ? Number((mm / 25.4).toFixed(2)) : Number(mm.toFixed(1)));
    } else if (rawKey.includes('wind')) {
      const mph = pf.windSpeedMph?.[sIdx] ?? 0;
      result.push(isImperial ? Math.round(mph) : Math.round(mph * 1.60934));
    } else if (rawKey.includes('sun_intensity') || rawKey.includes('solar') || rawKey.includes('irradiance') || rawKey.includes('sun_flux')) {
      result.push(pf.sunIntensityWm2?.[sIdx] ?? 0);
    } else if (rawKey.includes('aqi')) {
      result.push(pf.aqiValues?.[sIdx] ?? 0);
    } else if (rawKey.includes('smoke')) {
      result.push(pf.smokeSfc?.[sIdx] ?? 0);
    } else {
      const c = pf.tempsC?.[sIdx] ?? 0;
      result.push(isImperial ? Math.round(c * 1.8 + 32) : Math.round(c));
    }
  }

  return result;
}

export class MeteogramWidget implements WidgetRenderer {
  public renderHtml(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): string {
    const title = widget.title || 'Multi-Product Meteogram';
    const height = widget.options?.height || 220;
    const canvasId = `poi-meteo-canvas-${widget.id}`;
    const allowSourceSelection = widget.options?.allowSourceSelection !== false;
    const activeSourceKey = (widget.options?.dataSource && widget.options.dataSource !== 'auto')
      ? widget.options.dataSource
      : (ctx.activeTabId && ctx.activeTabId !== 'blend' ? ctx.activeTabId : 'auto');

    const lat = ctx.lat;
    const lon = ctx.lon;
    const isCA = lat >= 41.6 && lat <= 83.1 && lon >= -141.0 && lon <= -52.6 && (lat >= 49.0 || (lat >= 42.0 && lon < -79.0));
    const isUS = (lat >= 24.5 && lat <= 49.38 && lon >= -124.8 && lon <= -66.9) ||
                 (lat >= 51.0 && lat <= 71.5 && lon >= -179.0 && lon <= -129.0) ||
                 (lat >= 18.9 && lat <= 22.3 && lon >= -160.3 && lon <= -154.8);
    const autoAgency = isUS ? 'NWS' : (isCA ? 'ECCC' : 'ECMWF');

    return `
      <div class="metric-card" data-widget-id="${widget.id}" style="background: rgba(30, 41, 59, 0.75); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 14px 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.3);">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; flex-wrap: wrap; gap: 8px;">
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 0.95rem; font-weight: 700; color: #fff;">${title}</span>
            ${allowSourceSelection ? `
              <div style="display: inline-flex; align-items: center; gap: 4px;">
                <span style="font-size: 0.65rem; color: #94a3b8; font-weight: 600;">GLOBAL:</span>
                <select class="meteo-source-select" style="background: rgba(0, 212, 255, 0.12); color: #00d4ff; border: 1px solid rgba(0, 212, 255, 0.35); border-radius: 6px; padding: 2px 6px; font-size: 0.7rem; font-weight: 700; cursor: pointer; outline: none;">
                  <option value="auto" ${activeSourceKey === 'auto' ? 'selected' : ''}>Auto (${autoAgency})</option>
                  <option value="nws" ${activeSourceKey === 'nws' ? 'selected' : ''}>NWS (NOAA)</option>
                  <option value="eccc" ${activeSourceKey === 'eccc' ? 'selected' : ''}>ECCC (Canada)</option>
                  <option value="ecmwf" ${activeSourceKey === 'ecmwf' ? 'selected' : ''}>ECMWF (IFS)</option>
                  <option value="icon13" ${activeSourceKey === 'icon13' ? 'selected' : ''}>ICON-13 (DWD)</option>
                  <option value="hrrr" ${activeSourceKey === 'hrrr' ? 'selected' : ''}>HRRR (3km)</option>
                  <option value="rrfs" ${activeSourceKey === 'rrfs' ? 'selected' : ''}>RRFS (3km)</option>
                  <option value="rdps" ${activeSourceKey === 'rdps' ? 'selected' : ''}>RDPS (10km)</option>
                  <option value="gdps" ${activeSourceKey === 'gdps' ? 'selected' : ''}>GDPS (15km)</option>
                  <option value="gfs" ${activeSourceKey === 'gfs' ? 'selected' : ''}>GFS (0.25°)</option>
                </select>
              </div>
            ` : (widget.options?.dataSource && widget.options.dataSource !== 'auto' ? `
              <span style="font-size: 0.65rem; padding: 2px 6px; background: rgba(0, 212, 255, 0.12); color: #00d4ff; border-radius: 4px; font-weight: 700; border: 1px solid rgba(0, 212, 255, 0.25); letter-spacing: 0.3px;">${widget.options.dataSource.toUpperCase()}</span>
            ` : '')}
          </div>
        </div>

        <!-- Dual-Function Legend Container (Chips & Per-Series Model Selectors) -->
        <div class="meteogram-legend-bar" id="legend-bar-${widget.id}" style="display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 8px;">
          <!-- Dynamically populated in init() -->
        </div>

        <div style="height: ${height}px; position: relative; width: 100%; margin-top: 4px;">
          <canvas id="${canvasId}"></canvas>
        </div>
      </div>
    `;
  }

  public init(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): void {
    const canvasId = `poi-meteo-canvas-${widget.id}`;
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!canvas) return;

    const data = ctx.data;
    const labels = data?.timeLabels || [];

    if (!labels || labels.length === 0) {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.style.display = 'none';
        parent.innerHTML = TierAccessService.buildTrendChartDenialHtml({
          denial: data?.accessDenial || ForecastBinaryService.lastAccessDenial,
          requiredTier: 'pro',
          featureLabel: `${widget.title || 'hourly meteogram'} trend charts`
        });
      }
      return;
    }

    const isImperial = store.getState().units === 'imperial';
    const convT = (arr: number[] | undefined) => {
      if (!arr) return [];
      return isImperial ? arr : arr.map(f => Math.round((f - 32) * 5 / 9));
    };

    const allowSourceSelection = widget.options?.allowSourceSelection !== false;
    const activeSourceKey = (widget.options?.dataSource && widget.options.dataSource !== 'auto')
      ? widget.options.dataSource
      : (ctx.activeTabId && ctx.activeTabId !== 'blend' ? ctx.activeTabId : 'auto');

    const seriesConfig = widget.options?.series && widget.options.series.length > 0
      ? widget.options.series
      : [
          { key: 'temps_f', type: 'line', yAxis: 'left', color: '#f59e0b' },
          { key: 'precip_prob_pct', type: 'bar', yAxis: 'right', color: '#38bdf8' }
        ];

    const datasets: any[] = [];
    let hasRightAxis = false;
    let leftAxisLabel = '';
    let rightAxisLabel = '';
    let leftAxisIsSeeing = false;
    let rightAxisIsSeeing = false;

    seriesConfig.forEach((s: any) => {
      const rawKey = String(s.key || '').toLowerCase();
      const isRight = s.yAxis === 'right' || s.yAxisID === 'y1' || s.yAxisID === 'right';
      const yAxisID = isRight ? 'y1' : 'y';
      if (isRight) hasRightAxis = true;

      const color = s.color || (isRight ? '#38bdf8' : '#f59e0b');
      const isBar = s.type === 'bar';
      const isArea = s.type === 'area' || s.type === 'stacked_area';
      const isDashed = s.type === 'dashed';

      let seriesData: number[] = [];
      let label = s.label || DEFAULT_SERIES_LABELS[rawKey] || '';

      if (rawKey.includes('freezing_level') || rawKey.includes('freezing')) {
        const rawSeries: number[] = ((data as any).freezingLevels && (data as any).freezingLevels.length > 0)
          ? (data as any).freezingLevels
          : (data.soundings && data.soundings.length > 0)
            ? data.soundings.map(snd => snd.freezingLevelFt || data.avgFreezingElev || 8500)
            : labels.map(() => data.avgFreezingElev || 8500);
        seriesData = rawSeries.map((val: number) => isImperial ? Math.round(val) : Math.round(val / 3.28084));
        const avg = data.avgFreezingElev || (rawSeries.length ? Math.round(rawSeries.reduce((a: number, b: number) => a + b, 0) / rawSeries.length) : 8500);
        label = s.label || (isImperial ? `Freezing Level (~${Math.round(avg).toLocaleString()} ft)` : `Freezing Level (~${Math.round(avg / 3.28084).toLocaleString()} m)`);
        if (!isRight) leftAxisLabel = isImperial ? 'Altitude (ft MSL)' : 'Altitude (m)';
        else rightAxisLabel = isImperial ? 'Altitude (ft MSL)' : 'Altitude (m)';
      } else if (rawKey.includes('precip_rate') || rawKey.includes('rain_rate')) {
        seriesData = (data.precipRate || []).map(r => isImperial ? Number(r.toFixed(2)) : Number((r * 25.4).toFixed(1)));
        label = label || (isImperial ? 'Precip Rate (in/hr)' : 'Precip Rate (mm/hr)');
        if (isRight) rightAxisLabel = isImperial ? 'in/hr' : 'mm/hr';
        else leftAxisLabel = isImperial ? 'in/hr' : 'mm/hr';
      } else if (rawKey.includes('precip_prob') || rawKey.includes('precip_likelihood') || rawKey.includes('rain')) {
        seriesData = data.precipLikelihood || [];
        label = label || 'Precip Probability (%)';
        if (isRight) rightAxisLabel = '% Chance';
        else leftAxisLabel = '% Chance';
      } else if (rawKey.includes('thunderstorm') || rawKey.includes('tstorm') || rawKey.includes('lightning')) {
        seriesData = data.thunderstormProb || [];
        label = label || 'Thunderstorm Probability (%)';
        if (isRight) rightAxisLabel = '% Chance';
        else leftAxisLabel = '% Chance';
      } else if (rawKey.includes('highest') || rawKey.includes('summit')) {
        seriesData = convT(data.tempsHighest);
        label = label || 'Summit Temperature';
        if (!leftAxisLabel) leftAxisLabel = isImperial ? '°F' : '°C';
      } else if (rawKey.includes('lowest') || rawKey.includes('base')) {
        seriesData = convT(data.tempsLowest);
        label = label || 'Valley Base Temperature';
        if (!leftAxisLabel) leftAxisLabel = isImperial ? '°F' : '°C';
      } else if (rawKey.includes('mid')) {
        seriesData = convT(data.tempsMid);
        label = label || 'Mid-Mountain Temperature';
        if (!leftAxisLabel) leftAxisLabel = isImperial ? '°F' : '°C';
      } else if (rawKey.includes('temps') || rawKey.includes('temp')) {
        seriesData = convT(data.tempsForecast);
        label = label || 'Forecast Temperature';
        if (!leftAxisLabel) leftAxisLabel = isImperial ? '°F' : '°C';
      } else if (rawKey.includes('cloud_cover') || rawKey.includes('clouds')) {
        seriesData = data.cloudCover || [];
        label = label || 'Total Cloud Cover (%)';
        if (isRight) rightAxisLabel = 'Cloud %';
        else leftAxisLabel = 'Cloud %';
      } else if (rawKey.includes('wind_speed') || rawKey.includes('wind')) {
        seriesData = data.windSpeed || [];
        label = label || (isImperial ? 'Wind Speed (mph)' : 'Wind Speed (km/h)');
        if (isRight) rightAxisLabel = isImperial ? 'mph' : 'km/h';
        else leftAxisLabel = isImperial ? 'mph' : 'km/h';
      } else if (rawKey.includes('cloud_base')) {
        const baseEstimate = 6500;
        seriesData = labels.map(() => isImperial ? baseEstimate : Math.round(baseEstimate / 3.28084));
        label = label || 'Cloud Base (ft)';
        if (!leftAxisLabel) leftAxisLabel = isImperial ? 'Altitude (ft)' : 'Altitude (m)';
      } else if (rawKey.includes('cloud_top')) {
        const topEstimate = 11500;
        seriesData = labels.map(() => isImperial ? topEstimate : Math.round(topEstimate / 3.28084));
        label = label || 'Cloud Top (ft)';
        if (!leftAxisLabel) leftAxisLabel = isImperial ? 'Altitude (ft)' : 'Altitude (m)';
      } else if (rawKey.includes('darkskies') || rawKey.includes('darksky')) {
        seriesData = (data.darkskiesScores || []).map(v => v !== null && v !== undefined ? Number(v.toFixed(1)) : 0);
        label = label || 'Dark Skies Score (0-10)';
        if (isRight) rightAxisLabel = 'Score (0-10)';
        else leftAxisLabel = 'Score (0-10)';
      } else if (rawKey.includes('dew_risk') || rawKey.includes('dew')) {
        seriesData = data.dewRiskHourly || [];
        label = label || 'Optics Dew Risk (0-100)';
        if (isRight) rightAxisLabel = 'Dew Risk %';
        else leftAxisLabel = 'Dew Risk %';
      } else if (rawKey.includes('seeing')) {
        if (data.seeingArcsec && data.seeingArcsec.length > 0) {
          seriesData = data.seeingArcsec.map(v => v !== null && v !== undefined ? Number(v.toFixed(2)) : 2.5);
        } else if (data.seeingScores && data.seeingScores.length > 0) {
          seriesData = data.seeingScores.map(score => score !== null ? Number(seeingScoreToArcsec(score).toFixed(2)) : 2.5);
        } else {
          seriesData = (data.darkskiesScores || []).map(ds => {
            if (!ds || ds <= 0) return 3.5;
            return Number((1.0 + (10.0 - ds) * 0.25).toFixed(2));
          });
        }
        label = label || 'Seeing (arcsec)';
        if (isRight) {
          rightAxisLabel = 'arcsec (better ↑)';
          rightAxisIsSeeing = true;
        } else {
          leftAxisLabel = 'arcsec (better ↑)';
          leftAxisIsSeeing = true;
        }
      } else if (rawKey.includes('sun_intensity') || rawKey.includes('solar') || rawKey.includes('irradiance') || rawKey.includes('sun_flux')) {
        seriesData = data.sunIntensityHourly || [];
        label = label || 'Solar Irradiance (W/m²)';
        if (isRight) rightAxisLabel = 'W/m²';
        else leftAxisLabel = 'W/m²';
      } else if (rawKey.includes('aqi')) {
        seriesData = data.aqiValues || [];
        label = label || 'Air Quality Index (AQI)';
        if (isRight) rightAxisLabel = 'AQI';
        else leftAxisLabel = 'AQI';
      } else if (rawKey.includes('smoke')) {
        seriesData = data.smokeDensity || [];
        label = label || 'Ground Smoke PM2.5 (µg/m³)';
        if (isRight) rightAxisLabel = 'µg/m³';
        else leftAxisLabel = 'µg/m³';
      } else {
        seriesData = convT(data.tempsForecast);
      }

      if (!label) {
        label = rawKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      }

      datasets.push({
        label,
        data: seriesData,
        type: isBar ? 'bar' : 'line',
        backgroundColor: isBar ? (color.startsWith('#') ? color + '55' : 'rgba(56, 189, 248, 0.35)') : (isArea ? color + '22' : 'transparent'),
        borderColor: color,
        borderWidth: isBar ? 1 : 2,
        borderDash: isDashed ? [5, 4] : undefined,
        fill: isArea,
        pointRadius: isBar ? undefined : 0,
        tension: s.tension !== undefined ? s.tension : 0.3,
        yAxisID,
        order: isBar ? 2 : 1
      });
    });

    const useStaticYAxis = !!(widget.options?.staticYAxis || widget.options?.static_y_axis);
    const leftSeriesList = seriesConfig.filter((s: any) => s.yAxis !== 'right' && s.yAxisID !== 'y1' && s.yAxisID !== 'right');
    const rightSeriesList = seriesConfig.filter((s: any) => s.yAxis === 'right' || s.yAxisID === 'y1' || s.yAxisID === 'right');

    const isPctOrProb = (list: any[]) => list.some((s: any) => {
      const k = String(s.key || '').toLowerCase();
      return k.includes('prob') || k.includes('pct') || k.includes('cloud') || k.includes('humidity') || k.includes('rh') || k.includes('dew_risk') || k.includes('hazard') || k.includes('score') || k.includes('moon');
    });
    const isDarkSkies = (list: any[]) => list.some((s: any) => String(s.key || '').toLowerCase().includes('darkskies'));
    const isKp = (list: any[]) => list.some((s: any) => String(s.key || '').toLowerCase().includes('kp'));

    const scales: any = {
      x: {
        grid: { color: 'rgba(255, 255, 255, 0.05)' },
        ticks: { color: '#64748b', font: { size: 9 }, maxTicksLimit: 8 }
      },
      y: {
        position: 'left',
        reverse: leftAxisIsSeeing,
        grid: { color: 'rgba(255, 255, 255, 0.05)' },
        title: leftAxisLabel ? { display: true, text: leftAxisLabel, color: '#94a3b8', font: { size: 10 } } : undefined,
        ticks: {
          color: '#94a3b8',
          font: { size: 9 },
          callback: (val: any) => (useStaticYAxis && isPctOrProb(leftSeriesList) ? `${val}%` : val)
        }
      }
    };

    if (useStaticYAxis) {
      if (isPctOrProb(leftSeriesList)) {
        scales.y.min = 0;
        scales.y.max = 100;
        scales.y.ticks.stepSize = 25;
      } else if (isDarkSkies(leftSeriesList)) {
        scales.y.min = 0;
        scales.y.max = 10;
        scales.y.ticks.stepSize = 2;
      } else if (isKp(leftSeriesList)) {
        scales.y.min = 0;
        scales.y.max = 9;
        scales.y.ticks.stepSize = 3;
      }
    }

    if (hasRightAxis) {
      scales.y1 = {
        position: 'right',
        reverse: rightAxisIsSeeing,
        grid: { display: false },
        title: rightAxisLabel ? { display: true, text: rightAxisLabel, color: '#38bdf8', font: { size: 10 } } : undefined,
        ticks: {
          color: '#38bdf8',
          font: { size: 9 },
          callback: (val: any) => (useStaticYAxis && isPctOrProb(rightSeriesList) ? `${val}%` : val)
        }
      };
      if (useStaticYAxis) {
        if (isPctOrProb(rightSeriesList)) {
          scales.y1.min = 0;
          scales.y1.max = 100;
          scales.y1.ticks.stepSize = 25;
        } else if (isDarkSkies(rightSeriesList)) {
          scales.y1.min = 0;
          scales.y1.max = 10;
          scales.y1.ticks.stepSize = 2;
        } else if (isKp(rightSeriesList)) {
          scales.y1.min = 0;
          scales.y1.max = 9;
          scales.y1.ticks.stepSize = 3;
        }
      }
    }

    const chartInstance = new Chart(canvas, {
      type: 'line',
      data: {
        labels,
        datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        scales,
        plugins: {
          legend: {
            display: false // Use custom dual-function HTML legend chips above
          }
        }
      }
    });

    ctx.charts.push(chartInstance);

    // Build Dual-Function Legend Chips
    const legendBar = document.getElementById(`legend-bar-${widget.id}`);
    if (legendBar) {
      legendBar.innerHTML = '';
      datasets.forEach((ds, dsIdx) => {
        const sConf = seriesConfig[dsIdx] || {};
        const rawKey = String(sConf.key || '').toLowerCase();
        const catKey = getSeriesModelCategory(rawKey);
        const catConfig = catKey ? SERIES_SUPPORTED_MODELS[catKey] : null;
        const color = ds.borderColor || ds.backgroundColor || '#38bdf8';
        const isArea = ds.fill === true;

        const activeModel = sConf.model || (catConfig ? catConfig.default : (widget.options?.dataSource || 'auto'));

        const chip = document.createElement('div');
        chip.className = 'legend-chip';
        chip.dataset.datasetIndex = String(dsIdx);
        chip.style.cssText = `
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 3px 8px;
          background: rgba(15, 23, 42, 0.75);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 6px;
          cursor: pointer;
          user-select: none;
          transition: all 0.15s ease;
        `;

        // Swatch
        const swatch = document.createElement('span');
        swatch.className = 'legend-swatch';
        swatch.style.cssText = `
          width: 8px;
          height: 8px;
          border-radius: 2px;
          flex-shrink: 0;
          background: ${isArea ? 'transparent' : color};
          border: ${isArea ? `2px solid ${color}` : `1px solid ${color}`};
          box-shadow: 0 0 6px ${color}66;
        `;

        // Label
        const titleSpan = document.createElement('span');
        titleSpan.className = 'legend-label';
        titleSpan.style.cssText = `
          font-size: 0.74rem;
          font-weight: 600;
          color: #e2e8f0;
          white-space: nowrap;
        `;
        titleSpan.textContent = ds.label || sConf.label || rawKey;

        chip.appendChild(swatch);
        chip.appendChild(titleSpan);

        // Click to toggle visibility
        chip.addEventListener('click', (e) => {
          if ((e.target as HTMLElement).tagName.toLowerCase() === 'select') return;
          const isVisible = chartInstance.isDatasetVisible(dsIdx);
          chartInstance.setDatasetVisibility(dsIdx, !isVisible);
          chartInstance.update();
          if (isVisible) {
            chip.style.opacity = '0.4';
            chip.style.textDecoration = 'line-through';
          } else {
            chip.style.opacity = '1';
            chip.style.textDecoration = 'none';
          }
        });

        // Model selector dropdown if supported
        if (catConfig && catConfig.models && catConfig.models.length > 0 && allowSourceSelection) {
          const select = document.createElement('select');
          select.className = 'legend-model-select';
          select.style.cssText = `
            appearance: none;
            -webkit-appearance: none;
            border-radius: 4px;
            padding: 1px 18px 1px 6px;
            font-size: 0.65rem;
            font-weight: 700;
            cursor: pointer;
            outline: none;
            border: 1px solid ${color}55;
            background: rgba(15, 23, 42, 0.9);
            color: ${color};
            background-repeat: no-repeat;
            background-position: right 4px center;
            background-size: 8px 8px;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%2394a3b8'%3E%3Cpath fill-rule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z' clip-rule='evenodd'/%3E%3C/svg%3E");
          `;

          catConfig.models.forEach(m => {
            const opt = document.createElement('option');
            opt.value = m.id;
            opt.textContent = m.name;
            opt.selected = (m.id === activeModel);
            select.appendChild(opt);
          });

          select.addEventListener('click', (e) => e.stopPropagation());
          select.addEventListener('change', async (e) => {
            e.stopPropagation();
            const selectedModel = select.value;
            sConf.model = selectedModel;

            const manifest = ctx.manifest;
            if (!manifest) return;

            try {
              select.style.opacity = '0.5';
              const pointForecast = await ForecastBinaryService.getForecastForPoint(ctx.lat, ctx.lon, manifest, selectedModel);
              if (pointForecast) {
                const existingNotice = legendBar.parentElement?.querySelector('.meteo-tier-denial-notice');
                if (existingNotice) existingNotice.remove();
                const updatedSeries = extractSeriesDataForModel(rawKey, pointForecast, labels.length, (data as any).timelineStartMs, isImperial);
                if (updatedSeries && updatedSeries.length > 0) {
                  chartInstance.data.datasets[dsIdx].data = updatedSeries;
                  chartInstance.update();
                }
              } else if (ForecastBinaryService.lastAccessDenial || !TierAccessService.userHasLayerAccess('forecast_point_tiles')) {
                let noticeEl = legendBar.parentElement?.querySelector('.meteo-tier-denial-notice') as HTMLElement | null;
                if (!noticeEl && legendBar.parentElement) {
                  noticeEl = document.createElement('div');
                  noticeEl.className = 'meteo-tier-denial-notice';
                  noticeEl.style.marginBottom = '8px';
                  legendBar.insertAdjacentElement('afterend', noticeEl);
                }
                if (noticeEl) {
                  noticeEl.innerHTML = TierAccessService.buildTrendChartDenialHtml({
                    denial: ForecastBinaryService.lastAccessDenial,
                    requiredTier: 'pro',
                    featureLabel: `${selectedModel.toUpperCase()} model series`,
                    compact: true
                  });
                }
              }
            } catch (err) {
              console.warn('[MeteogramWidget] Failed to update series model:', err);
            } finally {
              select.style.opacity = '1';
            }
          });

          chip.appendChild(select);

          // If a non-default specific model was configured on this series, fetch and align initially
          const manifest = ctx.manifest;
          if (manifest && sConf.model && sConf.model !== 'auto' && sConf.model !== activeSourceKey) {
            ForecastBinaryService.getForecastForPoint(ctx.lat, ctx.lon, manifest, sConf.model).then(pf => {
              if (pf) {
                const updated = extractSeriesDataForModel(rawKey, pf, labels.length, (data as any).timelineStartMs, isImperial);
                if (updated && updated.length > 0) {
                  chartInstance.data.datasets[dsIdx].data = updated;
                  chartInstance.update();
                }
              }
            }).catch(e => console.warn('[MeteogramWidget] Failed to load initial model series:', e));
          }
        }

        legendBar.appendChild(chip);
      });
    }

    const container = document.querySelector(`[data-widget-id="${widget.id}"]`) || canvas.closest('.metric-card') || document;
    const sourceSelect = container.querySelector('.meteo-source-select') as HTMLSelectElement;
    if (sourceSelect) {
      sourceSelect.addEventListener('change', (e) => {
        e.stopPropagation();
        if (ctx.onSourceChange) {
          ctx.onSourceChange(sourceSelect.value);
        }
      });
    }
  }
}
